/**
 * `/oms/sends` — cross-customer sends history (OCP-135).
 *
 * Filters: customer (substring), status, region, since (ISO date).
 * Pagination: cursor-based.
 * Click-through: row → /oms/customers/:id for that send's customer.
 *
 * Operator UX: surfaces send status, region, message id so the
 * operator can correlate a "my mail didn't arrive" customer report
 * against actual delivery state.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { api, ApiError } from '../../lib/api';
import {
  StatusPill,
  UnconfiguredEmptyState,
  formatDate,
} from './CustomerDetailPage';

interface Send {
  id: string;
  customer_id?: string | null;
  customer_email?: string | null;
  from?: string | null;
  to?: string | null;
  subject?: string | null;
  status?: string | null;
  region?: string | null;
  message_id?: string | null;
  rejected_reason?: string | null;
  sent_at?: string | null;
}

interface SendsResponse {
  sends: Send[];
  cursor?: string | null;
}

const STATUS_OPTIONS = [
  '',
  'queued',
  'sent',
  'delivered',
  'bounced',
  'complained',
  'rejected',
  'failed',
] as const;

const REGION_OPTIONS = ['', 'eu-central-1', 'eu-west-1'] as const;

export function OmsSendsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [customer, setCustomer] = useState(params.get('customer') ?? '');
  const [status, setStatus] = useState(params.get('status') ?? '');
  const [region, setRegion] = useState(params.get('region') ?? '');
  const [since, setSince] = useState(params.get('since') ?? '');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [data, setData] = useState<SendsResponse | null>(null);
  const [state, setState] = useState<'loading' | 'idle' | 'error' | 'unconfigured'>('loading');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (customer) p.set('customer', customer);
    if (status) p.set('status', status);
    if (region) p.set('region', region);
    if (since) p.set('since', since);
    if (cursor) p.set('cursor', cursor);
    p.set('limit', '100');
    return p.toString();
  }, [customer, status, region, since, cursor]);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const r = await api<SendsResponse>(`/v1/admin/oms/sends?${qs}`);
      setData(r);
      setState('idle');
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setState('unconfigured');
        return;
      }
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; detail?: string } | null;
        setErrorDetail(body?.detail ?? body?.error ?? `HTTP ${err.status}`);
      } else {
        setErrorDetail(err instanceof Error ? err.message : 'Unknown');
      }
      setState('error');
    }
  }, [qs]);

  useEffect(() => {
    void load();
  }, [load]);

  // Reflect filters in the URL so deep-links + back-button work.
  useEffect(() => {
    const p = new URLSearchParams();
    if (customer) p.set('customer', customer);
    if (status) p.set('status', status);
    if (region) p.set('region', region);
    if (since) p.set('since', since);
    setParams(p, { replace: true });
  }, [customer, status, region, since, setParams]);

  if (state === 'unconfigured') return <UnconfiguredEmptyState />;

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">orboto Mail · Sends</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Cross-customer sends history. Click a row to jump to the customer's detail
          page.
        </p>
      </header>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <FilterText label="Customer (email/id)" value={customer} onChange={setCustomer} />
        <FilterSelect label="Status" value={status} options={STATUS_OPTIONS} onChange={setStatus} />
        <FilterSelect label="Region" value={region} options={REGION_OPTIONS} onChange={setRegion} />
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-gray-600 dark:text-gray-400">Since</span>
          <input
            type="date"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        {state === 'loading' && <span className="text-xs text-gray-500">Loading…</span>}
      </div>

      {state === 'error' && (
        <ErrorPanel detail={errorDetail} onRetry={() => void load()} />
      )}

      {data && data.sends.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Sent</th>
                <th className="px-3 py-2">Subject</th>
                <th className="px-3 py-2">From / To</th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {data.sends.map((s) => (
                <tr
                  key={s.id}
                  onClick={() =>
                    s.customer_id && navigate(`/oms/customers/${s.customer_id}`)
                  }
                  className={s.customer_id ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60' : ''}
                >
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-500">
                    {s.sent_at ? formatDate(s.sent_at) : '—'}
                  </td>
                  <td className="truncate px-3 py-2 font-medium">{s.subject ?? '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                    <div className="truncate">{s.from ?? '—'}</div>
                    <div className="truncate text-gray-400">→ {s.to ?? '—'}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                    {s.customer_email ?? s.customer_id?.slice(0, 8) ?? '—'}
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill status={s.status ?? null} />
                    {s.rejected_reason && (
                      <p className="mt-1 text-xs text-red-700 dark:text-red-300">{s.rejected_reason}</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        state === 'idle' && (
          <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
            No sends match the current filters.
          </div>
        )
      )}

      {data?.cursor && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => setCursor(data.cursor ?? undefined)}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Load more →
          </button>
        </div>
      )}
    </div>
  );
}

function FilterText({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-gray-600 dark:text-gray-400">{label}</span>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-56 rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
      />
    </label>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-gray-600 dark:text-gray-400">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o || 'All'}
          </option>
        ))}
      </select>
    </label>
  );
}

function ErrorPanel({
  detail,
  onRetry,
}: {
  detail: string | null;
  onRetry: () => void;
}) {
  return (
    <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-red-900 dark:text-red-200">Could not load sends</h2>
          {detail && <p className="mt-1 break-all text-xs text-red-800 dark:text-red-300">{detail}</p>}
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="rounded border border-red-300 dark:border-red-700 bg-white dark:bg-gray-900 px-2 py-0.5 text-xs text-red-700 dark:text-red-300 hover:bg-red-100"
        >
          Retry
        </button>
      </div>
    </div>
  );
}
